/*
 * Beauty Editorial Experience — animation/behavior layer.
 * Namespaced under window.BME. Originally scoped to a single #bme-root
 * wrapping one giant section; now the "Beauty & Makeup" page is built from
 * many independent Shopify sections, each wrapping its own piece of markup
 * in its own ".bme-root" scope (for the shared CSS custom properties) —
 * so every init routine here works off class selectors across the whole
 * document instead of one fixed id, and is re-runnable per section
 * instance so it keeps working when sections are added/removed/reordered/
 * edited in the Theme Editor.
 *
 * Degrades gracefully if GSAP fails to load or reduced-motion is requested.
 */
(function () {
  if (window.BME && window.BME.__initialized) return;
  window.BME = window.BME || {};
  window.BME.__initialized = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = window.matchMedia('(pointer: coarse)').matches;
  var hasGSAP = !!(window.gsap && window.ScrollTrigger);

  if (hasGSAP) {
    gsap.registerPlugin(ScrollTrigger);
    // iOS/Android fire a resize event when the address bar shows/hides
    // mid-scroll; without this, that resize can shift a pinned section's
    // start/end math and make it feel like it "jumps" or sticks.
    ScrollTrigger.config({ ignoreMobileResize: true });
  }

  /* ---------------------------------------------------------------------
   * Per-section cleanup registry — prevents duplicate ScrollTriggers /
   * infinite tweens / observers from piling up when the Theme Editor
   * reloads, reorders, or removes a section.
   * ------------------------------------------------------------------- */
  var registry = new Map(); // sectionWrapperEl -> [killFn, ...]

  function track(wrapperEl, killFn) {
    if (!wrapperEl || !killFn) return;
    var list = registry.get(wrapperEl);
    if (!list) { list = []; registry.set(wrapperEl, list); }
    list.push(killFn);
  }

  function cleanup(wrapperEl) {
    var list = registry.get(wrapperEl);
    if (!list) return;
    list.forEach(function (fn) {
      try { fn(); } catch (e) { /* no-op */ }
    });
    registry.delete(wrapperEl);
  }

  function wrapperOf(el) {
    return (el && el.closest && el.closest('.shopify-section')) || document.body;
  }

  /* ---------------------------------------------------------------------
   * Helper: find matching elements within a scope, including the scope
   * element itself if it matches (scope is either `document` on first
   * boot, or a specific freshly-loaded section wrapper).
   * ------------------------------------------------------------------- */
  function findAll(scope, selector) {
    var out = [];
    if (scope !== document && scope.nodeType === 1 && scope.matches && scope.matches(selector)) {
      out.push(scope);
    }
    if (scope.querySelectorAll) {
      out = out.concat(Array.prototype.slice.call(scope.querySelectorAll(selector)));
    }
    return out;
  }

  /* ---------------------------------------------------------------------
   * Scroll reveals (IntersectionObserver) — lightweight, used everywhere.
   * ------------------------------------------------------------------- */
  function initReveals(scope) {
    var items = findAll(scope, '.bme-reveal');
    if (!items.length) return;

    // assign stagger index within each .bme-stagger container in scope
    findAll(scope, '.bme-stagger').forEach(function (group) {
      var children = Array.prototype.slice.call(group.querySelectorAll('.bme-reveal'));
      children.forEach(function (el, i) { el.style.setProperty('--bme-i', i); });
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    items.forEach(function (el) {
      io.observe(el);
      track(wrapperOf(el), function () { io.unobserve(el); });
    });
    track(wrapperOf(items[0]), function () { io.disconnect(); });
  }

  /* ---------------------------------------------------------------------
   * Hero: word reveal + floating shapes + parallax on scroll out
   * ------------------------------------------------------------------- */
  function initHero(hero) {
    if (!hero || hero.dataset.bmeInit) return;
    hero.dataset.bmeInit = '1';
    var wrapper = wrapperOf(hero);

    requestAnimationFrame(function () {
      hero.classList.add('is-ready');
    });

    if (reduceMotion) return;

    if (hasGSAP) {
      // floating shapes — transform-only tweens, cheap on every device,
      // kept running on touch too so the composition matches desktop.
      hero.querySelectorAll('.bme-hero__shape').forEach(function (shape, i) {
        var tw = gsap.to(shape, {
          y: (isTouch ? 14 : 26) + i * 6,
          x: (i % 2 === 0 ? 1 : -1) * (isTouch ? 8 : 14),
          rotation: i % 2 === 0 ? 8 : -8,
          duration: 6 + i,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true
        });
        track(wrapper, function () { tw.kill(); });
      });

      // scroll-out cinematic transition
      var head = hero.querySelector('.bme-hero__head');
      var visual = hero.querySelector('.bme-hero__visual');
      var sub = hero.querySelector('.bme-hero__sub');

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6
        }
      })
        .to(head, { y: -60, opacity: 0.35, ease: 'none' }, 0)
        .to(sub, { y: -30, opacity: 0, ease: 'none' }, 0)
        .to(visual, { y: 40, scale: 0.94, ease: 'none' }, 0);
      track(wrapper, function () {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.kill();
      });
    }

    // lightweight particle drift (CSS-free, cheap rAF-driven GSAP tweens).
    // Count scales down on narrow screens (less density needed visually)
    // but the drift animation itself stays on for every device.
    var particleField = hero.querySelector('.bme-hero__particles');
    if (particleField && !particleField.dataset.bmeSeeded) {
      particleField.dataset.bmeSeeded = '1';
      var count = window.innerWidth < 600 ? 8 : 16;
      for (var i = 0; i < count; i++) {
        var p = document.createElement('span');
        p.className = 'bme-hero__particle';
        p.style.left = Math.random() * 100 + '%';
        p.style.top = Math.random() * 100 + '%';
        p.style.opacity = (0.2 + Math.random() * 0.4).toFixed(2);
        particleField.appendChild(p);
        if (hasGSAP) {
          var pt = gsap.to(p, {
            y: -(30 + Math.random() * 60),
            x: (Math.random() - 0.5) * 40,
            duration: 6 + Math.random() * 6,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: Math.random() * 3
          });
          track(wrapper, function () { pt.kill(); });
        }
      }
    }
  }

  /* ---------------------------------------------------------------------
   * A–Z section: a pinned vertical scroll drives horizontal track motion
   * (scrub) — identical technique on desktop, tablet AND touch phones.
   * Vertical finger movement is what drives the horizontal translation;
   * the user never swipes sideways. The pin releases automatically once
   * the horizontal sequence finishes, and reverses cleanly on scroll-up
   * since it's driven by scroll position, not a one-shot animation.
   *
   * Only two situations fall back to something else:
   *  - GSAP/ScrollTrigger failed to load: no scrub is possible at all, so
   *    the row becomes a native horizontally-swipeable strip instead of
   *    disabling the section outright.
   *  - prefers-reduced-motion: the row lays out statically (wraps), no
   *    scroll-driven motion, honoring the user's OS-level preference.
   * ------------------------------------------------------------------- */
  function initAtoZ(section) {
    if (!section || section.dataset.bmeInit) return;
    section.dataset.bmeInit = '1';
    var wrapper = wrapperOf(section);

    var pin = section.querySelector('.bme-atoz__pin');
    var track_ = section.querySelector('.bme-atoz__track');
    if (!pin || !track_) return;

    if (!hasGSAP) {
      section.classList.add('bme-atoz--swipe');

      if (reduceMotion || !('IntersectionObserver' in window)) {
        return;
      }

      track_.classList.add('bme-reveal', 'bme-reveal--scale');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      io.observe(track_);
      track(wrapper, function () { io.disconnect(); });
      return;
    }

    if (reduceMotion) {
      // GSAP is available but the OS asked for reduced motion: keep the
      // horizontal track but drop the pin+scrub animation, let it wrap.
      pin.style.height = 'auto';
      pin.style.overflow = 'visible';
      track_.style.position = 'relative';
      track_.style.flexWrap = 'wrap';
      return;
    }

    // Vertical-only touch intent — the pin+scrub math below is what
    // converts that vertical motion into horizontal translation, so the
    // browser never needs to interpret a sideways gesture here.
    pin.style.touchAction = 'pan-y';

    function getDistance() {
      return Math.max(0, track_.scrollWidth - window.innerWidth + window.innerWidth * 0.08);
    }

    var tween = gsap.to(track_, {
      x: function () { return -getDistance(); },
      ease: 'none',
      scrollTrigger: {
        trigger: pin,
        start: 'top top',
        end: function () { return '+=' + (getDistance() + window.innerHeight * 0.4); },
        scrub: 0.6,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });

    track(wrapper, function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
    });
  }

  /* ---------------------------------------------------------------------
   * Statement section: progressive word "lighting" on scroll
   * ------------------------------------------------------------------- */
  function initStatement(el) {
    if (!el || el.dataset.bmeSplit) return;
    el.dataset.bmeSplit = '1';
    var wrapper = wrapperOf(el);

    var words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map(function (w) {
      return '<span class="bme-w">' + w + '</span>';
    }).join(' ');

    var spans = el.querySelectorAll('.bme-w');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      spans.forEach(function (s) { s.classList.add('is-lit'); });
      return;
    }

    if (hasGSAP) {
      var tw = gsap.to(spans, {
        opacity: 1,
        stagger: 0.06,
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          end: 'bottom 40%',
          scrub: 0.5
        }
      });
      track(wrapper, function () {
        if (tw.scrollTrigger) tw.scrollTrigger.kill();
        tw.kill();
      });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-lit');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
      spans.forEach(function (s) { io.observe(s); });
      track(wrapper, function () { io.disconnect(); });
    }
  }

  /* ---------------------------------------------------------------------
   * Eyes section: floating chips
   * ------------------------------------------------------------------- */
  function initFloaters(eyesSection) {
    if (!hasGSAP || reduceMotion || !eyesSection || eyesSection.dataset.bmeFloatInit) return;
    eyesSection.dataset.bmeFloatInit = '1';
    var wrapper = wrapperOf(eyesSection);

    var floaters = eyesSection.querySelectorAll('.bme-eyes__float');
    var amplitude = isTouch ? 8 : 14;
    floaters.forEach(function (f, i) {
      var tw = gsap.to(f, {
        y: (i % 2 === 0 ? 1 : -1) * (amplitude + i * 2),
        duration: 4.5 + i * 0.6,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true
      });
      track(wrapper, function () { tw.kill(); });
    });
  }

  /* ---------------------------------------------------------------------
   * Tilt cards: subtle cursor-follow tilt on pointer devices only.
   * Listeners live on elements that get discarded with their section on
   * reload/removal, so nothing to explicitly clean up here.
   * ------------------------------------------------------------------- */
  function initTiltCards(scope) {
    if (reduceMotion || isTouch) return;
    var cards = findAll(scope, '.bme-trending__ph, .bme-card__media');
    cards.forEach(function (card) {
      if (card.dataset.bmeTilt) return;
      card.dataset.bmeTilt = '1';
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = 'perspective(600px) rotateX(' + (py * -4) + 'deg) rotateY(' + (px * 4) + 'deg)';
      });
      card.addEventListener('pointerleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Run every init routine across a scope (document on first boot, or a
   * single freshly-(re)loaded section wrapper from the Theme Editor).
   * ------------------------------------------------------------------- */
  function initAll(scope) {
    scope = scope || document;
    initReveals(scope);
    findAll(scope, '.bme-hero').forEach(initHero);
    findAll(scope, '.bme-atoz').forEach(initAtoZ);
    findAll(scope, '.bme-statement__text').forEach(initStatement);
    findAll(scope, '.bme-eyes').forEach(initFloaters);
    initTiltCards(scope);
  }

  /* ---------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------- */
  function boot() {
    initAll(document);

    if (hasGSAP) {
      // Recalculate pinned distances after fonts/layout settle.
      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      });
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () { ScrollTrigger.refresh(); }, 200);
      });
    }

    // Theme Editor: each Beauty & Makeup section is now independently
    // editable/reorderable/removable. Re-init just the affected section on
    // load, tear down its tweens/observers/triggers on unload, and nudge
    // ScrollTrigger to recompute pinned positions after a drag-reorder —
    // this is what keeps animations correct without duplicating triggers
    // or leaking GSAP tweens tied to DOM nodes that no longer exist.
    document.addEventListener('shopify:section:unload', function (e) {
      cleanup(e.target);
    });
    document.addEventListener('shopify:section:load', function (e) {
      cleanup(e.target); // defensive: in case unload didn't fire for this node
      initAll(e.target);
      if (hasGSAP) ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:reorder', function () {
      if (hasGSAP) ScrollTrigger.refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
